import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tabs,
  Tab,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText as MUIListItemText,
  ListItemAvatar,
  Collapse,
  FormControlLabel,
  Checkbox,
  Switch,
  CircularProgress,
} from '@mui/material';
import MenuTreeComponent from "../../components/features/menus/MenuTreeComponent";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Print as PrintIcon,
  Menu as MenuIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Article as PageIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Assessment as ReportIcon,
  AccountBalance as FinanceIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ProcurementIcon,
  EventNote as EventIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  DragIndicator as DragIcon,
  Sort as SortIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  Star as StarIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  AddOutlined,
  ListOutlined,
  ApprovalOutlined,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/authSlice';
import { useAuth } from '../../components/features/auth/AuthGuard';
import { ROUTES, ROLES, PERMISSIONS } from '../../constants';
import useNotification from '../../hooks/common/useNotification';
import useConfirmDialog from '../../hooks/common/useConfirmDialog';
import { LoadingSpinner } from '../../components/common/Loading';
import menusAPI from '../../services/api/menus.api';

const MenusPage = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { hasPermission, hasAnyRole, isAdmin } = useAuth();
  const { showSuccess, showError, showWarning } = useNotification();
  const { isOpen, config, openDialog, closeDialog, handleConfirm } =
    useConfirmDialog();

  // State management
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    menu_name: "",
    menu_label: "",
    menu_icon: "",
    menu_url: "",
    parent_id: null,
    menu_order: 0,
    is_active: true,
    description: "",
    menu_type: "page",
    external_url: false,
    target: "_self",
    css_class: "",
    requires_auth: true,
  });

  // Menu types
  const menuTypes = [
    {
      value: "page",
      label: "Page",
      icon: <PageIcon />,
      description: "Regular page menu item",
    },
    {
      value: "folder",
      label: "Folder",
      icon: <FolderIcon />,
      description: "Parent menu with children",
    },
    {
      value: "divider",
      label: "Divider",
      icon: <DragIcon />,
      description: "Visual separator",
    },
    {
      value: "external",
      label: "External Link",
      icon: <LinkIcon />,
      description: "External website link",
    },
  ];

  // Available icons - Using comprehensive icon list from constants
  const availableIcons = [
    // Main Navigation
    "DashboardOutlined",
    "HomeWorkOutlined",
    "MonitorOutlined",
    
    // People & HR Management
    "PeopleOutlined",
    "PersonOutlined",
    "ContactsOutlined",
    "PersonAddOutlined",
    "ManageAccountsOutlined",
    "GroupOutlined",
    "GroupAddOutlined",
    "BadgeOutlined",
    "WorkOutlined",
    
    // Time & Attendance
    "AccessTimeOutlined",
    "EventNoteOutlined",
    "CalendarTodayOutlined",
    "ScheduleOutlined",
    "TimelapseOutlined",
    "EventAvailableOutlined",
    "EventBusyOutlined",
    
    // Departments & Organization
    "CorporateFareOutlined",
    "ApartmentOutlined",
    "LocationCityOutlined",
    "BusinessCenterOutlined",
    
    // Finance & Accounting
     "AccountBalanceOutlined",
     "AccountBalanceWalletOutlined",
     "PaymentsOutlined",
     "CreditCardOutlined",
     "ReceiptOutlined",
     "MonetizationOnOutlined",
     "SavingsOutlined",
     "ReceiptLongOutlined",
     "RequestQuoteOutlined",
     "PieChartOutlined",
     "MenuBookOutlined",
     "SummarizeOutlined",
     "AddOutlined",
     "ListOutlined",
     "ApprovalOutlined",
    
    // Documents & Files
    "FolderOutlined",
    "DocumentScannerOutlined",
    "DescriptionOutlined",
    "FileCopyOutlined",
    "CloudUploadOutlined",
    
    // Reports & Analytics
    "AssessmentOutlined",
    "BarChartOutlined",
    "TrendingUpOutlined",
    "AnalyticsOutlined",
    "InsightsOutlined",
    
    // Settings & Administration
    "SettingsOutlined",
    "TuneOutlined",
    "BuildOutlined",
    "IntegrationInstructionsOutlined",
    "AdminPanelSettingsOutlined",
    
    // Security & Access
    "SecurityOutlined",
    "SupervisedUserCircleOutlined",
    "VpnKeyOutlined",
    "ShieldOutlined",
    "LockOutlined",
    
    // Inventory & Assets
    "InventoryOutlined",
    "CategoryOutlined",
    "StoreOutlined",
    
    // Procurement & Purchasing
    "RequestQuoteOutlined",
    "ShoppingCartOutlined",
    "LocalShippingOutlined",
    
    // Meetings & Events
    "MeetingRoomOutlined",
    "TaskOutlined",
    "AssignmentTurnedInOutlined",
    
    // Travel & Expenses
     "FlightTakeoffOutlined",
     "HotelOutlined",
     "DirectionsCarOutlined",
     "LuggageOutlined",
    
    // General
    "SettingsOutlined",
  ];

  // Build hierarchical menu tree
  const buildMenuTree = (menus, parentId = null) => {
    return menus
      .filter((menu) => menu.parent_id === parentId)
      .sort((a, b) => a.menu_order - b.menu_order)
      .map((menu) => ({
        ...menu,
        children: buildMenuTree(menus, menu.id),
      }));
  };

  // Summary cards data (calculated from actual data)
  const summaryCards = [
    {
      title: "Total Menus",
      value: menus.length.toString(),
      subtitle: `${menus.filter((m) => m.is_active).length} active`,
      icon: <MenuIcon />,
      color: "primary",
    },
    {
      title: "Parent Menus",
      value: menus.filter((menu) => menu.parent_id === null).length.toString(),
      subtitle: "Top-level navigation",
      icon: <FolderIcon />,
      color: "success",
    },
    {
      title: "Submenus",
      value: menus.filter((menu) => menu.parent_id !== null).length.toString(),
      subtitle: "Nested menu items",
      icon: <PageIcon />,
      color: "info",
    },
  ];


  // Load data on component mount
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await fetchMenus();
    } catch (error) {
      console.error("Error loading initial data:", error);
      showError("Failed to load application data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const response = await menusAPI.getAllMenus({
        include_hierarchy: true,
        include_children: true,
      });
  
      if (response.success) {
        // Handle both flat and hierarchical responses
        let menuData;
        
        if (response.data?.flat) {
          // New API format with both hierarchy and flat
          menuData = response.data.flat;
          console.log('Using flat menu data:', menuData.length, 'menus');
        } else if (response.data?.hierarchy) {
          // Flatten the hierarchy for the menu list
          const flattenHierarchy = (hierarchicalMenus) => {
            const flattened = [];
            hierarchicalMenus.forEach(menu => {
              flattened.push(menu);
              if (menu.children && menu.children.length > 0) {
                flattened.push(...flattenHierarchy(menu.children));
              }
            });
            return flattened;
          };
          menuData = flattenHierarchy(response.data.hierarchy);
          console.log('Flattened hierarchical menu data:', menuData.length, 'menus');
        } else if (Array.isArray(response.data)) {
          // Simple array format
          menuData = response.data;
          console.log('Using direct array menu data:', menuData.length, 'menus');
        } else {
          throw new Error('Unexpected menu data format');
        }
  
        // Add level calculation for better display
        const menusWithLevels = menuData.map(menu => ({
          ...menu,
          level: menu.parent_id === null ? 0 : 1, // Simple level calculation
        }));
  
        setMenus(menusWithLevels);
        console.log('Menus loaded successfully:', menusWithLevels.length);
      } else {
        throw new Error(response.message || 'Failed to fetch menus');
      }
    } catch (error) {
      console.error('Error fetching menus:', error);
      showError(error.userMessage || 'Failed to fetch menus');
      setMenus([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Add this function for menu reordering
  const handleMenuReorder = async (menuId, newOrder, newParentId = null) => {
    try {
      const response = await menusAPI.updateMenuOrder(menuId, {
        menu_order: newOrder,
        parent_id: newParentId,
      });

      if (response.success) {
        showSuccess("Menu order updated successfully");
        await fetchMenus(); // Refresh the tree
      } else {
        throw new Error(response.message || "Failed to update menu order");
      }
    } catch (error) {
      console.error("Error updating menu order:", error);
      showError("Failed to update menu order");
    }
  };


  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadInitialData();
      showSuccess("Data refreshed successfully");
    } catch (error) {
      showError("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setSaving(true);

      // Validate required fields
      if (!formData.menu_name.trim()) {
        showWarning("Menu name is required");
        return;
      }
      if (!formData.menu_label.trim()) {
        showWarning("Menu label is required");
        return;
      }

      const menuData = {
        ...formData,
      };

      let response;
      if (editingMenu) {
        response = await menusAPI.updateMenu(editingMenu.id, menuData);
      } else {
        response = await menusAPI.createMenu(menuData);
      }

      if (response.success) {
        showSuccess(
          editingMenu
            ? "Menu updated successfully"
            : "Menu created successfully"
        );
        setDialogOpen(false);
        resetForm();
        await fetchMenus(); // Refresh menu list
      } else {
        throw new Error(response.message || "Failed to save menu");
      }
    } catch (error) {
      console.error("Error saving menu:", error);
      showError(error.userMessage || "Failed to save menu");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      menu_name: "",
      menu_label: "",
      menu_icon: "",
      menu_url: "",
      parent_id: null,
      menu_order: 0,
      is_active: true,
      description: "",
      menu_type: "page",
      external_url: false,
      target: "_self",
      css_class: "",
      requires_auth: true,
    });
    setEditingMenu(null);
  };

  // Handle edit
  const handleEdit = (menu) => {
    setFormData({
      ...menu,
      parent_id: menu.parent_id || null,
    });
    setEditingMenu(menu);
    setDialogOpen(true);
    setAnchorEl(null);
  };

  // Handle delete
  const handleDelete = (menu) => {
    openDialog({
      title: "Delete Menu",
      message: `Are you sure you want to delete menu "${menu.menu_label}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await menusAPI.deleteMenu(menu.id);
          if (response.success) {
            showSuccess("Menu deleted successfully");
            await fetchMenus(); // Refresh menu list
          } else {
            throw new Error(response.message || "Failed to delete menu");
          }
        } catch (error) {
          console.error("Error deleting menu:", error);
          showError(error.userMessage || "Failed to delete menu");
        }
      },
    });
    setAnchorEl(null);
  };


  // Toggle menu status
  const handleToggleStatus = async (menu) => {
    try {
      const response = await menusAPI.updateMenu(menu.id, {
        is_active: !menu.is_active,
      });

      if (response.success) {
        showSuccess(
          `Menu ${!menu.is_active ? "activated" : "deactivated"} successfully`
        );
        await fetchMenus(); // Refresh menu list
      } else {
        throw new Error(response.message || "Failed to update menu status");
      }
    } catch (error) {
      console.error("Error updating menu status:", error);
      showError(error.userMessage || "Failed to update menu status");
    }
    setAnchorEl(null);
  };

  // Filter menus
  const filteredMenus = menus.filter((menu) => {
    const matchesSearch =
      menu.menu_label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      menu.menu_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (menu.menu_url &&
        menu.menu_url.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus =
      !statusFilter ||
      (statusFilter === "active" && menu.is_active) ||
      (statusFilter === "inactive" && !menu.is_active);
    const matchesType = !typeFilter || menu.menu_type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Render menu tree
  const renderTreeItem = (node) => (
    <TreeItem
      key={node.id}
      nodeId={node.id.toString()}
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
          {node.menu_type === "folder" ? (
            <FolderIcon color="primary" />
          ) : (
            <PageIcon color="action" />
          )}
          <Typography variant="body2" fontWeight="medium">
            {node.menu_label}
          </Typography>
          <Chip
            label={node.menu_type}
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
          />
          {!node.is_active && (
            <Chip
              label="Inactive"
              size="small"
              color="default"
              variant="filled"
            />
          )}
        </Box>
      }
    >
      {node.children && node.children.map((child) => renderTreeItem(child))}
    </TreeItem>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h4">Menu Management</Typography>
          <Button
            variant="outlined"
            startIcon={
              refreshing ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage navigation menu structure, labels, icons, URLs, and hierarchy
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="h6" component="div">
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.subtitle}
                    </Typography>
                  </Box>
                  <Box sx={{ color: `${card.color}.main` }}>{card.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Content */}
      <Card>
        <CardContent>
          {/* Filters and Actions */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search menus..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {menuTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box
                  sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                >
                  {hasPermission(PERMISSIONS.MANAGE_MENUS) && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setDialogOpen(true)}
                    >
                      Add Menu
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<SortIcon />}
                    onClick={async () => {
                      try {
                        // Simple reorder by menu_order
                        const sortedMenus = [...menus].sort(
                          (a, b) => a.menu_order - b.menu_order
                        );

                        // Update order numbers
                        for (let i = 0; i < sortedMenus.length; i++) {
                          if (sortedMenus[i].menu_order !== i + 1) {
                            await handleMenuReorder(sortedMenus[i].id, i + 1);
                          }
                        }

                        showSuccess("Menus reordered successfully");
                      } catch (error) {
                        showError("Failed to reorder menus");
                      }
                    }}
                  >
                    Reorder
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      /* Export menus */
                    }}
                  >
                    Export
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Content Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          >
            <Tab label="Menu List" />
            <Tab label="Menu Tree" />
          </Tabs>

          {/* Menu List Tab */}
          {activeTab === 0 && (
            <Box sx={{ mt: 2 }}>
              <MenuTreeComponent
                menus={menus}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onMenuAction={(menu, action, event) => {
                  switch (action) {
                    case "toggle-status":
                      handleToggleStatus(menu);
                      break;
                    case "menu":
                      setAnchorEl(event.currentTarget);
                      setSelectedMenu(menu);
                      break;
                    case "create":
                      setDialogOpen(true);
                      resetForm();
                      break;
                    case "edit":
                      handleEdit(menu);
                      break;
                    case "updated":
                    case "created":
                    case "status-toggled":
                      // Refresh menus after action
                      fetchMenus();
                      break;
                    default:
                      console.log("Unknown action:", action);
                  }
                }}
              />
            </Box>
          )}

          {/* Menu Tree Tab */}
          {activeTab === 1 && (
            <Box sx={{ mt: 2 }}>
              <MenuTreeComponent
                menus={menus}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onMenuAction={(menu, action, event) => {
                  switch (action) {
                    case "toggle-status":
                      handleToggleStatus(menu);
                      break;
                    case "menu":
                      setAnchorEl(event.currentTarget);
                      setSelectedMenu(menu);
                      break;
                    case "create":
                      setDialogOpen(true);
                      resetForm();
                      break;
                    default:
                      console.log("Unknown action:", action);
                  }
                }}
              />
            </Box>
          )}

          {/* Permission Matrix Tab - Removed: Use Role Management page for permissions */}
          {activeTab === 2 && (
            <Box sx={{ mt: 2, p: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Permission Matrix has been moved</strong>
                </Typography>
                <Typography variant="body2">
                  To manage permissions for menus, please use the <strong>Role Management</strong> page. 
                  There you can assign permissions to roles using the new permission tree view.
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/settings/roles')}
                startIcon={<SecurityIcon />}
              >
                Go to Role Management
              </Button>
            </Box>
          )}
          
          {/* Old Permission Matrix Code - Removed */}
          {false && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  Menu-Role Permission Matrix
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    onClick={async () => {
                      try {
                        // Refresh the permission matrix
                        await fetchPermissionMatrix();
                        showSuccess("Permission matrix refreshed successfully");
                      } catch (error) {
                        showError("Failed to refresh permission matrix");
                      }
                    }}
                    startIcon={<RefreshIcon />}
                  >
                    Refresh
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      // Grant all permissions to Admin role (role ID 1)
                      const newMatrix = { ...menuPermissions };
                      filteredMenus.forEach((menu) => {
                        newMatrix[`1-${menu.id}`] = true;
                      });
                      setMenuPermissions(newMatrix);
                    }}
                    startIcon={<SecurityIcon />}
                  >
                    Grant Admin Full Access
                  </Button>
                </Box>
              </Box>

              {/* Permission Matrix Table */}
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: 600 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          minWidth: 200,
                          fontWeight: "bold",
                          bgcolor: "background.paper",
                        }}
                      >
                        Menu / Submenu
                      </TableCell>
                      {roles.map((role) => (
                        <TableCell
                          key={role.id}
                          align="center"
                          sx={{
                            minWidth: 120,
                            fontWeight: "bold",
                            bgcolor: "background.paper",
                            writingMode:
                              role.role_name.length > 20
                                ? "vertical-rl"
                                : "horizontal-tb",
                            textOrientation:
                              role.role_name.length > 20 ? "mixed" : "initial",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="caption" fontWeight="bold">
                              {role.role_name}
                            </Typography>
                            <Chip
                              label={`ID: ${role.id}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.6rem", height: 16 }}
                            />
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMenus.map((menu) => {
                      // Determine if this is a parent menu or submenu
                      const isParentMenu = menu.parent_id === null;
                      const isSubmenu = menu.parent_id !== null;
                      const parentMenu = isSubmenu
                        ? filteredMenus.find((m) => m.id === menu.parent_id)
                        : null;

                      return (
                        <TableRow
                          key={menu.id}
                          sx={{
                            "&:hover": { bgcolor: "action.hover" },
                            bgcolor: isParentMenu
                              ? "action.selected"
                              : "inherit",
                          }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                pl: isSubmenu ? 3 : 0, // Indent submenus
                              }}
                            >
                              {/* Indentation line for submenus */}
                              {isSubmenu && (
                                <Box
                                  sx={{
                                    width: 2,
                                    height: 20,
                                    bgcolor: "divider",
                                    position: "absolute",
                                    left: 16,
                                  }}
                                />
                              )}

                              {/* Menu icon */}
                              {menu.menu_type === "folder" || isParentMenu ? (
                                <FolderIcon color="primary" fontSize="small" />
                              ) : (
                                <PageIcon color="action" fontSize="small" />
                              )}

                              {/* Menu details */}
                              <Box>
                                <Typography
                                  variant="body2"
                                  fontWeight={isParentMenu ? "bold" : "medium"}
                                >
                                  {menu.menu_label}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {menu.menu_name}
                                  </Typography>
                                  {menu.menu_url && (
                                    <>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        •
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="primary.main"
                                        fontFamily="monospace"
                                      >
                                        {menu.menu_url}
                                      </Typography>
                                    </>
                                  )}
                                  {isSubmenu && parentMenu && (
                                    <>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        •
                                      </Typography>
                                      <Chip
                                        label={`Under: ${parentMenu.menu_label}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 16, fontSize: "0.6rem" }}
                                      />
                                    </>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Permission checkboxes for each role */}
                          {roles.map((role) => {
                            const permissionKey = `${role.id}-${menu.id}`;
                            const hasPermission =
                              menuPermissions[permissionKey] || false;

                            return (
                              <TableCell key={role.id} align="center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={hasPermission}
                                    onChange={async (e) => {
                                      try {
                                        const newAccess = e.target.checked;

                                        // Update backend
                                        const response =
                                          await menusAPI.updateRoleMenuAccess(
                                            role.id,
                                            menu.id,
                                            { can_access: newAccess }
                                          );

                                        if (response.success) {
                                          // Update local state
                                          setMenuPermissions((prev) => ({
                                            ...prev,
                                            [permissionKey]: newAccess,
                                          }));

                                          showSuccess(
                                            `${role.role_name} access to "${menu.menu_label}" ${newAccess ? "granted" : "removed"}`
                                          );
                                        } else {
                                          throw new Error(
                                            response.message ||
                                              "Failed to update access"
                                          );
                                        }
                                      } catch (error) {
                                        console.error(
                                          "Error updating menu access:",
                                          error
                                        );
                                        showError(
                                          `Failed to update access: ${error.message}`
                                        );

                                        // Revert checkbox state on error
                                        e.target.checked = !e.target.checked;
                                      }
                                    }}
                                    sx={{
                                      color: hasPermission
                                        ? "success.main"
                                        : "action.disabled",
                                      "&.Mui-checked": {
                                        color: "success.main",
                                      },
                                    }}
                                  />

                                  {/* Visual indicator */}
                                  {hasPermission && (
                                    <Chip
                                      label="✓"
                                      size="small"
                                      color="success"
                                      sx={{
                                        height: 16,
                                        fontSize: "0.6rem",
                                        minWidth: 16,
                                        "& .MuiChip-label": { px: 0.5 },
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Matrix Summary */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Permission Matrix Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Menus: <strong>{filteredMenus.length}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Roles: <strong>{roles.length}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Combinations:{" "}
                      <strong>{filteredMenus.length * roles.length}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Active Permissions:{" "}
                      <strong>
                        {Object.values(menuPermissions).filter(Boolean).length}
                      </strong>
                    </Typography>
                  </Grid>
                </Grid>

                {/* Legend */}
                <Box
                  sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Legend:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Checkbox size="small" checked readOnly />
                    <Typography variant="caption">Has Access</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Checkbox size="small" readOnly />
                    <Typography variant="caption">No Access</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <FolderIcon fontSize="small" color="primary" />
                    <Typography variant="caption">Parent Menu</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PageIcon fontSize="small" color="action" />
                    <Typography variant="caption">Submenu/Page</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setViewDialogOpen(true);
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {hasPermission(PERMISSIONS.MANAGE_MENUS) && (
          <MenuItem onClick={() => handleEdit(selectedMenu)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Menu</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleToggleStatus(selectedMenu)}>
          <ListItemIcon>
            {selectedMenu?.is_active ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedMenu?.is_active ? "Deactivate" : "Activate"}
          </ListItemText>
        </MenuItem>
        <Divider />
        {(() => {
          // Check all possible permission formats
          // Based on migration, permissions are created as: menu_name:action (e.g., "menus:delete")
          // Backend also returns permission.name which is the same format
          const hasDeletePermission = 
            hasPermission('menus:delete') ||
            hasPermission('Menus:delete') ||
            hasPermission('Menus:Delete') ||
            hasPermission(PERMISSIONS.DELETE_MENU) || 
            hasPermission(PERMISSIONS.MENUS_DELETE) ||
            // Fallback: check if any permission contains "delete" and "menu"
            (user?.permissions?.some(p => {
              const perm = typeof p === 'string' ? p : (p.name || `${p.module}:${p.action}`);
              if (!perm) return false;
              const permLower = perm.toLowerCase();
              return (permLower.includes('delete') && permLower.includes('menu')) ||
                     permLower === 'menus:delete';
            }));
          
          return hasDeletePermission;
        })() && (
          <MenuItem
            onClick={() => handleDelete(selectedMenu)}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Menu</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Menu Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingMenu ? "Edit Menu" : "Add New Menu"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Menu Name *"
                value={formData.menu_name}
                onChange={(e) =>
                  setFormData({ ...formData, menu_name: e.target.value })
                }
                placeholder="e.g., dashboard, employees_list"
                helperText="Unique identifier for the menu (lowercase, underscore separated)"
                required
                error={!formData.menu_name.trim()}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Menu Label *"
                value={formData.menu_label}
                onChange={(e) =>
                  setFormData({ ...formData, menu_label: e.target.value })
                }
                placeholder="e.g., Dashboard, Employee List"
                helperText="Display name shown in navigation"
                required
                error={!formData.menu_label.trim()}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Menu Type *</InputLabel>
                <Select
                  value={formData.menu_type}
                  onChange={(e) =>
                    setFormData({ ...formData, menu_type: e.target.value })
                  }
                  label="Menu Type *"
                  required
                >
                  {menuTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {type.icon}
                        <Box>
                          <Typography variant="body2">{type.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {type.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Parent Menu</InputLabel>
                <Select
                  value={formData.parent_id || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      parent_id: e.target.value || null,
                    })
                  }
                  label="Parent Menu"
                >
                  <MenuItem value="">
                    <em>None (Top Level)</em>
                  </MenuItem>
                  {menus
                    .filter(
                      (menu) =>
                        menu.menu_type === "folder" &&
                        menu.id !== editingMenu?.id
                    )
                    .map((menu) => (
                      <MenuItem key={menu.id} value={menu.id}>
                        {menu.menu_label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* URL and Navigation */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, mt: 2, fontWeight: "bold" }}
              >
                URL and Navigation
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Menu URL"
                value={formData.menu_url}
                onChange={(e) =>
                  setFormData({ ...formData, menu_url: e.target.value })
                }
                placeholder="/dashboard, /employees, /finance/budgets"
                helperText="Route path (leave empty for folders)"
                disabled={formData.menu_type === "folder"}
                InputProps={{
                  startAdornment: formData.menu_url && (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Menu Order"
                type="number"
                value={formData.menu_order}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    menu_order: parseInt(e.target.value) || 0,
                  })
                }
                helperText="Display order"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  value={formData.menu_icon}
                  onChange={(e) =>
                    setFormData({ ...formData, menu_icon: e.target.value })
                  }
                  label="Icon"
                >
                  <MenuItem value="">
                    <em>No Icon</em>
                  </MenuItem>
                  {availableIcons.map((icon) => (
                    <MenuItem key={icon} value={icon}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CodeIcon fontSize="small" />
                        <Typography variant="body2">{icon}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Target</InputLabel>
                <Select
                  value={formData.target}
                  onChange={(e) =>
                    setFormData({ ...formData, target: e.target.value })
                  }
                  label="Target"
                >
                  <MenuItem value="_self">Same Window</MenuItem>
                  <MenuItem value="_blank">New Window</MenuItem>
                  <MenuItem value="_parent">Parent Frame</MenuItem>
                  <MenuItem value="_top">Top Frame</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Settings */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, mt: 2, fontWeight: "bold" }}
              >
                Settings
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requires_auth}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        requires_auth: e.target.checked,
                      })
                    }
                  />
                }
                label="Requires Authentication"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CSS Class"
                value={formData.css_class}
                onChange={(e) =>
                  setFormData({ ...formData, css_class: e.target.value })
                }
                placeholder="custom-menu-class"
                helperText="Optional CSS class for styling"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                multiline
                rows={3}
                placeholder="Brief description of this menu item..."
              />
            </Grid>

            {/* Access Control - Removed: Use Role Management page to assign permissions */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Note:</strong> To assign permissions to roles for this menu, 
                  go to <strong>Role Management</strong> page and use the permission tree view.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setDialogOpen(false);
                    navigate('/settings/roles');
                  }}
                  startIcon={<SecurityIcon />}
                  sx={{ mt: 1 }}
                >
                  Go to Role Management
                </Button>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving ? "Saving..." : editingMenu ? "Update Menu" : "Create Menu"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Menu Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Menu Details - {selectedMenu?.menu_label}</DialogTitle>
        <DialogContent>
          {selectedMenu && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Menu Name
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedMenu.menu_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Menu Type
                  </Typography>
                  <Chip
                    label={
                      menuTypes.find((t) => t.value === selectedMenu.menu_type)
                        ?.label
                    }
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    URL
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2 }}
                    fontFamily="monospace"
                  >
                    {selectedMenu.menu_url || "No URL (Folder)"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedMenu.is_active ? "Active" : "Inactive"}
                    size="small"
                    color={selectedMenu.is_active ? "success" : "default"}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Order
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedMenu.menu_order}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Level
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedMenu.level || 0}{" "}
                    {(selectedMenu.level || 0) === 0
                      ? "(Top Level)"
                      : "(Sub Menu)"}
                  </Typography>
                </Grid>
                {selectedMenu.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedMenu.description}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Note:</strong> To manage permissions for this menu, 
                      go to <strong>Role Management</strong> page and use the permission tree view.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {hasPermission(PERMISSIONS.MANAGE_MENUS) && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                setViewDialogOpen(false);
                handleEdit(selectedMenu);
              }}
            >
              Edit Menu
            </Button>
          )}
        </DialogActions>
      </Dialog>


      {/* Confirm Dialog */}
      {isOpen && (
        <Dialog open={isOpen} onClose={closeDialog}>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogContent>
            <Typography>{config.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleConfirm} color="error" variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default MenusPage;